import { Alert, Empty, Tooltip } from "antd"
import { ContestConfigLiveData } from "domains/managingJson/store/types"
import { resolvePeriodLabel } from "./resolvePeriodLabel"
import moment from "moment"
import { FC, useEffect, useState } from "react"

type DateValue = Date | string | undefined

type SegmentKind = "week" | "gap"

type TimelineSegment = {
  kind: SegmentKind
  label: string
  startMs: number
  endMs: number
}

type OverlapIssue = {
  message: string
}

type WeekWindow = {
  originalIndex: number
  periodName?: string
  startMs: number
  endMs: number
}

const SEGMENT_COLORS: Record<SegmentKind, string> = {
  week: "#1677ff",
  gap: "#faad14",
}

const formatUtc = (timestamp: number) => moment(timestamp).utc().format("YYYY-MM-DD HH:mm:ss [UTC]")

const formatMilestone = (timestamp: number) => moment(timestamp).utc().format("MMM D, YYYY HH:mm [UTC]")

const formatDuration = (durationMs: number) => {
  const duration = moment.duration(durationMs)
  const days = Math.floor(duration.asDays())
  const hours = duration.hours()
  const minutes = duration.minutes()
  const seconds = duration.seconds()

  const parts: string[] = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}m`)
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`)

  return parts.join(" ")
}

const toTimestamp = (value: DateValue): number | null => {
  if (!value) return null

  const parsed = moment(value)
  if (!parsed.isValid()) return null

  return parsed.valueOf()
}

const buildTimelineData = (
  weeks: ContestConfigLiveData[],
) => {
  const invalidDateWarnings: string[] = []

  const weekWindows: WeekWindow[] = weeks.reduce<WeekWindow[]>((acc, week, index) => {
    const startMs = toTimestamp(week.startDate as DateValue)
    const endMs = toTimestamp(week.endDate as DateValue)
    const label = resolvePeriodLabel(week.periodName, index)

    if (startMs === null || endMs === null) {
      invalidDateWarnings.push(`${label} has an invalid start or end date.`)

      return acc
    }

    if (endMs <= startMs) {
      invalidDateWarnings.push(`${label} end must be later than its start.`)

      return acc
    }

    acc.push({ originalIndex: index, periodName: week.periodName, startMs, endMs })

    return acc
  }, [])

  const sortedWeeks = [...weekWindows].sort((a, b) => a.startMs - b.startMs)
  const overlapIssues: OverlapIssue[] = []
  const segments: TimelineSegment[] = []

  sortedWeeks.forEach((week, index) => {
    const currentLabel = resolvePeriodLabel(week.periodName, week.originalIndex)
    segments.push({
      kind: "week",
      label: currentLabel,
      startMs: week.startMs,
      endMs: week.endMs,
    })

    const nextWeek = sortedWeeks[index + 1]
    if (!nextWeek) return

    if (nextWeek.startMs > week.endMs) {
      const nextLabel = resolvePeriodLabel(nextWeek.periodName, nextWeek.originalIndex)
      segments.push({
        kind: "gap",
        label: `Mid-period pause (${currentLabel} -> ${nextLabel})`,
        startMs: week.endMs,
        endMs: nextWeek.startMs,
      })
    } else if (nextWeek.startMs < week.endMs) {
      const nextLabel = resolvePeriodLabel(nextWeek.periodName, nextWeek.originalIndex)
      overlapIssues.push({ message: `${currentLabel} overlaps ${nextLabel}.` })
    }
  })

  const normalizedSegments = segments
    .filter(segment => segment.endMs > segment.startMs)
    .sort((a, b) => a.startMs - b.startMs)

  return { segments: normalizedSegments, overlapIssues, invalidDateWarnings }
}

export const SchedulingTimeline: FC<{
  weeks: ContestConfigLiveData[]
}> = ({ weeks }) => {
  const [now, setNow] = useState(() => Date.now())
  const { segments, overlapIssues, invalidDateWarnings } = buildTimelineData(weeks)

  const rangeStart = segments.length ? Math.min(...segments.map(segment => segment.startMs)) : null
  const rangeEnd = segments.length ? Math.max(...segments.map(segment => segment.endMs)) : null
  const totalRangeMs = rangeStart !== null && rangeEnd !== null ? rangeEnd - rangeStart : 0

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60_000)

    return () => clearInterval(interval)
  }, [])

  const nowPositionPercent
    = rangeStart !== null && rangeEnd !== null && totalRangeMs > 0
      ? Math.min(100, Math.max(0, ((now - rangeStart) / totalRangeMs) * 100))
      : null

  return (
    <div>
      {overlapIssues.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: "12px" }}
          message="Timeline warnings"
          description={overlapIssues.map(issue => issue.message).join(" ")}
        />
      )}
      {segments.length > 0 && invalidDateWarnings.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: "12px" }}
          message="Data notes"
          description={invalidDateWarnings.join(" ")}
        />
      )}

      {segments.length === 0 || rangeStart === null || totalRangeMs <= 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No valid schedule periods to visualize yet."
        />
      ) : (
        <>
          <div style={{ position: "relative", marginBottom: "12px" }}>
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "stretch",
                height: "44px",
                border: "1px solid #f0f0f0",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              {segments.map((segment, index) => {
                const durationMs = segment.endMs - segment.startMs
                const widthPercent = (durationMs / totalRangeMs) * 100
                const displayLabel = segment.kind === "week" ? segment.label : ""

                return (
                  <Tooltip
                    key={`${segment.kind}-${segment.startMs}-${segment.endMs}-${index}`}
                    title={
                      <div style={{ minWidth: "220px" }}>
                        <div style={{ fontWeight: 600, marginBottom: "8px" }}>{segment.label}</div>
                        <div style={{ fontSize: "12px", lineHeight: 1.6 }}>
                          <div><span style={{ color: "rgba(255,255,255,0.7)" }}>Start</span> {formatUtc(segment.startMs)}</div>
                          <div><span style={{ color: "rgba(255,255,255,0.7)" }}>End</span> {formatUtc(segment.endMs)}</div>
                          <div style={{ marginTop: "4px" }}>
                            <span style={{ color: "rgba(255,255,255,0.7)" }}>Duration</span> {formatDuration(durationMs)}
                          </div>
                        </div>
                      </div>
                    }
                  >
                    <div
                      style={{
                        width: `${widthPercent}%`,
                        minWidth: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: SEGMENT_COLORS[segment.kind],
                        color: "#ffffff",
                        fontSize: "11px",
                        borderRight: "1px solid rgba(255,255,255,0.4)",
                        padding: "0 4px",
                      }}
                    >
                      {displayLabel}
                    </div>
                  </Tooltip>
                )
              })}
            </div>
            {nowPositionPercent !== null && (
              <Tooltip title={`Now: ${formatMilestone(now)}`}>
                <div
                  style={{
                    position: "absolute",
                    left: `${nowPositionPercent}%`,
                    top: 0,
                    bottom: 0,
                    width: "12px",
                    transform: "translateX(-50%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "default",
                  }}
                >
                  <div
                    style={{
                      width: 0,
                      height: 0,
                      borderLeft: "8px solid transparent",
                      borderRight: "8px solid transparent",
                      borderTop: "14px solid #000",
                    }}
                  />
                  <div
                    style={{
                      flex: 1,
                      width: 2,
                      minHeight: 0,
                      background: "#000",
                      borderRadius: "2px",
                      marginTop: -5,
                    }}
                  />
                </div>
              </Tooltip>
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "12px",
              color: "#8c8c8c",
              marginBottom: "12px",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            <span title={formatUtc(rangeStart!)}>Start: {formatMilestone(rangeStart!)}</span>
            <span title={formatUtc(rangeEnd!)}>End: {formatMilestone(rangeEnd!)}</span>
          </div>

          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", fontSize: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ width: "10px", height: "10px", borderRadius: "2px", background: SEGMENT_COLORS.week }} />
              Periods
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ width: "10px", height: "10px", borderRadius: "2px", background: SEGMENT_COLORS.gap }} />
              Mid-period gap (no active period)
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  width: "4px",
                  height: "12px",
                  background: "#fff",
                  borderRadius: "2px",
                  boxShadow: "0 0 0 2px rgba(212, 212, 212, 0.8)",
                }}
              />
              Now
            </div>
          </div>
        </>
      )}
    </div>
  )
}
